import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ComponentConfig, DropZone } from '@measured/puck';
import CustomTooltip from '../../../components/common/tooltip';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { WidthProps, widthField } from '../../puck/reusable-props/width';
import {
  PaddingProps,
  paddingFields,
  PaddingType,
} from '../../puck/reusable-props/padding';
import ArrowDown from '../../../assets/svg/ArrowDown';
import {
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { ReduxSelectorProps } from '../../services/constants';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { resolveShowComponentField, getBorderProps } from '../../utils/helper';
import Border, { IBorderValueProps } from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import '../text-editor/style.scss';
import WithAccordion from '../customComponents/CustomAccordion';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PaddingComponent from '../customComponents/PaddingComponent';

export type CarouselContainer = {
  padding: PaddingType;
};

export type CarouselContainerProps = {
  panels: CarouselContainer[];
  autoSlide: boolean;
  slideInterval: number;
  pauseOnHover: boolean;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  customiseArrows: {
    isShowDots: boolean;
    isShowArrows: boolean;
    arrowColor: string;
  };
} & MarginProps &
  showComponentProps &
  WidthProps &
  PaddingProps;

export const CarouselContainer = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<CarouselContainerProps> => {
  return {
    label: (
      <CustomTooltip title="Carousel Panel">
        <span>Carousel Container</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      panels: {
        type: 'array',
        label: 'Panels',
        arrayFields: {
          padding: {
            type: 'custom',
            label: 'Padding',
            render: ({ value, onChange }) => (
              <PaddingComponent
                value={value}
                onChange={onChange}
                title="Padding"
              />
            ),
          },
        },
        defaultItemProps: {
          padding: {
            deviceType: 'desktop',
            paddingBottom: { desktop: '16', mobile: '12', tablet: '14' },
            paddingLeft: { desktop: '16', mobile: '12', tablet: '14' },
            paddingRight: { desktop: '16', mobile: '12', tablet: '14' },
            paddingTop: { desktop: '16', mobile: '12', tablet: '14' },
          },
        },
      },
      autoSlide: {
        type: 'radio',
        label: 'Auto Slide',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      slideInterval: {
        type: 'number',
        label: 'Slide Interval (ms)',
        min: 1,
        max: 10000,
      },
      pauseOnHover: {
        type: 'radio',
        label: 'Pause on Hover',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      customiseArrows: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Navigation Options">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Arrows</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title="Toggle to show navigation arrows"
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  checked={value?.isShowArrows}
                  onChange={event =>
                    onChange({
                      ...value,
                      isShowArrows: event.target.checked,
                    })
                  }
                  inputProps={{ 'aria-label': 'Toggle arrows' }}
                />
              </div>
            </div>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Dots</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title="Toggle to show navigation dots"
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  checked={value?.isShowDots}
                  onChange={event =>
                    onChange({
                      ...value,
                      isShowDots: event.target.checked,
                    })
                  }
                  inputProps={{ 'aria-label': 'Toggle dots' }}
                />
              </div>
            </div>
            <ColorPicker
              label="Arrow Color"
              tooltip="Select navigation arrow color"
              onChange={color =>
                onChange({
                  ...value,
                  arrowColor: color,
                })
              }
              value={value?.arrowColor}
            />
          </WithAccordion>
        ),
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...backgroungFields,
      ...widthField,
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      panels: [
        {
          padding: {
            deviceType: 'desktop',
            paddingBottom: { desktop: '16', mobile: '12', tablet: '14' },
            paddingLeft: { desktop: '16', mobile: '12', tablet: '14' },
            paddingRight: { desktop: '16', mobile: '12', tablet: '14' },
            paddingTop: { desktop: '16', mobile: '12', tablet: '14' },
          },
        },
        {
          padding: {
            deviceType: 'desktop',
            paddingBottom: { desktop: '16', mobile: '12', tablet: '14' },
            paddingLeft: { desktop: '16', mobile: '12', tablet: '14' },
            paddingRight: { desktop: '16', mobile: '12', tablet: '14' },
            paddingTop: { desktop: '16', mobile: '12', tablet: '14' },
          },
        },
      ],
      autoSlide: true,
      slideInterval: 3000,
      pauseOnHover: true,
      border: {
        deviceType: 'desktop',
        borderBottomLeft: { desktop: '15', tablet: '15', mobile: '15' },
        borderBottomRight: { desktop: '15', tablet: '15', mobile: '15' },
        borderTopLeft: { desktop: '15', tablet: '15', mobile: '15' },
        borderTopRight: { desktop: '15', tablet: '15', mobile: '15' },
        borderTopWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderRightWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderBottomWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderLeftWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderStyle: {
          desktop: 'border-solid',
          tablet: 'border-solid',
          mobile: 'border-solid',
        },
        borderColor: {
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
      },
      background: { color: '#000000', isInherit: false },
      margin: {
        desktop: { top: '16', bottom: '16', right: '0', left: '0' },
        tablet: { top: '16', bottom: '16', right: '0', left: '0' },
        mobile: { top: '12', bottom: '12', right: '0', left: '0' },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: { desktop: '0', mobile: '0', tablet: '0' },
        paddingLeft: { desktop: '0', mobile: '0', tablet: '0' },
        paddingRight: { desktop: '0', mobile: '0', tablet: '0' },
        paddingTop: { desktop: '0', mobile: '0', tablet: '0' },
      },
      width: 'w-full',
      customiseArrows: {
        isShowDots: true,
        isShowArrows: true,
        arrowColor: '#ffffff',
      },
    },
    resolveFields: ({ props }, { fields }) =>
      resolveShowComponentField(props.id, fields),
    render: ({
      panels,
      autoSlide,
      slideInterval,
      pauseOnHover,
      margin,
      padding,
      width,
      border,
      background,
      showComponent,
      editMode,
      customiseArrows,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [currentIndex, setCurrentIndex] = useState(0);
      const [isPaused, setIsPaused] = useState(false);
      const panelRef = useRef<HTMLDivElement>(null);
      const touchStartX = useRef<number | null>(null);
      const touchEndX = useRef<number | null>(null);

      const goToPrevious = useCallback(() => {
        setCurrentIndex(prev => (prev === 0 ? panels.length - 1 : prev - 1));
      }, [panels.length]);

      const goToNext = useCallback(() => {
        setCurrentIndex(prev => (prev + 1) % panels.length);
      }, [panels.length]);

      const goToPanel = useCallback((index: number) => {
        setCurrentIndex(index);
      }, []);

      useEffect(() => {
        if (!autoSlide || panels.length <= 1 || isPaused) return;

        const interval = setInterval(goToNext, slideInterval);
        return () => clearInterval(interval);
      }, [autoSlide, slideInterval, panels.length, isPaused, goToNext]);

      const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
      }, []);

      const handleTouchMove = useCallback((e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
      }, []);

      const handleTouchEnd = useCallback(() => {
        if (touchStartX.current !== null && touchEndX.current !== null) {
          const diff = touchStartX.current - touchEndX.current;
          if (Math.abs(diff) > 50) {
            if (diff > 0) goToNext();
            else goToPrevious();
          }
        }
        touchStartX.current = null;
        touchEndX.current = null;
      }, [goToNext, goToPrevious]);

      if (!panels?.length) {
        return (
          <div className="w-full text-center p-4 bg-gray-100 rounded">
            No panels to display
          </div>
        );
      }

      const panelWidth = panelRef.current?.offsetWidth || 0;

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`relative ${width} overflow-hidden text-editor-content min-h-12 rounded-lg`}
              style={{
                ...marginStyles,
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
                ...paddingStyles,
                ...getBorderProps(border, currentDeviceType),
              }}
              onMouseEnter={() => pauseOnHover && setIsPaused(true)}
              onMouseLeave={() => pauseOnHover && setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              role="region"
              aria-label="Carousel">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * panelWidth}px)`,
                }}>
                {panels.map((panel, index) => (
                  <div
                    key={index}
                    ref={index === 0 ? panelRef : null}
                    className={`w-full flex-shrink-0 flex`}
                    style={{
                      minWidth: '100%',
                      ...getPaddingStyles(panel?.padding, currentDeviceType),
                    }}
                    role="group"
                    aria-label={`Slide ${index + 1} of ${panels.length}`}
                    aria-hidden={index !== currentIndex}>
                    {(index === currentIndex ||
                      index === (currentIndex + 1) % panels.length ||
                      index ===
                        (currentIndex - 1 + panels.length) % panels.length) && (
                      <DropZone
                        zone={`CarouselContainer-${index}`}
                        className="w-full"
                      />
                    )}
                  </div>
                ))}
              </div>

              {panels.length > 1 && (
                <>
                  {customiseArrows?.isShowArrows && (
                    <>
                      <button
                        type="button"
                        onClick={goToPrevious}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 transition-colors"
                        aria-label="Previous slide">
                        <span
                          style={{
                            display: 'inline-block',
                            transform: 'rotate(90deg)',
                          }}>
                          <ArrowDown fill={customiseArrows?.arrowColor} />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={goToNext}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 transition-colors"
                        aria-label="Next slide">
                        <span
                          style={{
                            display: 'inline-block',
                            transform: 'rotate(270deg)',
                          }}>
                          <ArrowDown fill={customiseArrows?.arrowColor} />
                        </span>
                      </button>
                    </>
                  )}
                  {customiseArrows?.isShowDots && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
                      {panels.map((_, index) => (
                        <button
                          type="button"
                          key={index}
                          onClick={() => goToPanel(index)}
                          className={`w-3 h-3 rounded-full transition-colors ${
                            currentIndex === index ? 'bg-white' : 'bg-white/50'
                          } hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-white`}
                          aria-label={`Go to slide ${index + 1}`}
                          aria-current={currentIndex === index}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      );
    },
  };
};
